/* eslint-disable react-refresh/only-export-components */

import type { FC, ReactNode } from "react";

import { MobileOverlayRouteType } from "components/constants";
import { t } from "i18n/i18n";
import {
  ChatWithLinesIcon,
  CircleQuestionIcon,
  ContactsIcon,
  FolderIcon,
  InstructorNotesIcon,
  LongFormPencilIcon,
  PageListIcon,
  PaperClipLinkIcon,
  PersonClassIcon,
  PersonSingleIcon,
  RobotAIIcon,
} from "mds/icons";
import { CONTENT_SIDEBAR_TAB_NAME, PAGE_PATH_REGEX } from "utils/urls";

export const FLOATING_TABS = [
  CONTENT_SIDEBAR_TAB_NAME.AiAssistant,
  CONTENT_SIDEBAR_TAB_NAME.Users,
  CONTENT_SIDEBAR_TAB_NAME.InstructorNotes,
];

export const SIDEBAR_PANEL_ICONS: Record<CONTENT_SIDEBAR_TAB_NAME, FC> = {
  Topics: PageListIcon,
  Assignment: LongFormPencilIcon,
  CourseResource: PaperClipLinkIcon,
  InstructorWorkspace: FolderIcon,
  Planner: ContactsIcon,
  InstructorNotes: InstructorNotesIcon,
  AiAssistant: RobotAIIcon,
  Users: PersonClassIcon,
  Profile: PersonSingleIcon,
  HelpCenter: CircleQuestionIcon,
  ClassChat: ChatWithLinesIcon,
};

export const OVERLAY_TAB_MAP: Record<MobileOverlayRouteType, CONTENT_SIDEBAR_TAB_NAME | undefined> =
  {
    menu: undefined,
    planner: CONTENT_SIDEBAR_TAB_NAME.Planner,
    instructor_notes: CONTENT_SIDEBAR_TAB_NAME.InstructorNotes,
    ai: CONTENT_SIDEBAR_TAB_NAME.AiAssistant,
    profile: CONTENT_SIDEBAR_TAB_NAME.Profile,
    users: CONTENT_SIDEBAR_TAB_NAME.Users,
    chat: CONTENT_SIDEBAR_TAB_NAME.ClassChat,
  };

export const TAB_OVERLAY_MAP: Record<CONTENT_SIDEBAR_TAB_NAME, MobileOverlayRouteType> =
  Object.fromEntries(
    Object.entries(OVERLAY_TAB_MAP)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => value)
      .map(([key, value]) => [value, key]),
  ) as Record<CONTENT_SIDEBAR_TAB_NAME, MobileOverlayRouteType>;

// This is only used for the Amplitude event names.
export const TAB_EVENT_NAMES: Record<CONTENT_SIDEBAR_TAB_NAME, string> = {
  Topics: "Sidebar - Topics",
  Planner: "Sidebar - Planner",
  InstructorNotes: "Sidebar - Notes",
  AiAssistant: "Sidebar - AI assistant",
  Users: "Sidebar - Users",
  Profile: "Sidebar - Profile",
  HelpCenter: "Sidebar - Help center",
  Assignment: "Sidebar - Assignment",
  CourseResource: "Sidebar - Course resource",
  InstructorWorkspace: "Sidebar - Instructor workspace",
  ClassChat: "Sidebar - Class chat",
};

// These log names can be enforced in Typescript later.
export const SIDEBAR_EXPAND_EVENT_NAME = "Sidebar - Expand";
export const SIDEBAR_COLLAPSE_EVENT_NAME = "Sidebar - Collapse";

export const TabKeys = Object.keys(SIDEBAR_PANEL_ICONS);

export type TabProps = {
  [Tab in CONTENT_SIDEBAR_TAB_NAME as `${Tab}Panel`]?: ReactNode;
};

export const getSidebarHeaderText = (tab: CONTENT_SIDEBAR_TAB_NAME) =>
  t(`content_sidebar.title.${tab}`);

export const getSidebarHeader = (tab: CONTENT_SIDEBAR_TAB_NAME) => (
  <h3 className="mb-0 text-black-tint-20">{getSidebarHeaderText(tab)}</h3>
);

export const isFloatingTab = (tab: CONTENT_SIDEBAR_TAB_NAME) =>
  FLOATING_TABS.includes(tab) && PAGE_PATH_REGEX.test(window.location.pathname);
